import React from "react";
import "./Help.css";
import { FaLock, FaInfoCircle, FaEnvelope, FaBuilding } from "react-icons/fa";

const HelpPrivacyNotice = () => {
  return (
    <>
    <section className="helpSection p-3">
      <div className="container-xl">
        <div className="helpHeader mb-4">
          <h2 className="fw-bold text-dark">Privacy Notice</h2>
          <p className="text-muted">
            YOVHO is dedicated to safeguarding your personal data. This notice explains how we collect, use, and protect your information.
          </p>
        </div>

        <div className="helpContent">

          {/* 1 */}
          <div className="helpItem">
            <div className="helpIcon"><FaBuilding /></div>
            <div className="helpText">
              <h5>About YOVHO</h5>
              <p>
                In this notice, “YOVHO”, “YOVHO Rooms”, "we", "our", or "us" refer to YOVHO Stays UAE Pte. Limited, its parent organization, and affiliated entities (collectively the “YOVHO Group”). For contact details, email: <a href="mailto:info@yovho.com">info@yovho.com</a>.
              </p>
              <p>
                Depending on how you interact with us—app, website, YOVHO-branded property, or third-party booking—different YOVHO entities may manage your data. Contact us for specifics.
              </p>
            </div>
          </div>

          {/* 2 */}
          <div className="helpItem">
            <div className="helpIcon"><FaInfoCircle /></div>
            <div className="helpText">
              <h5>Scope of This Privacy Notice</h5>
              <p>
                This notice applies to anyone engaging with YOVHO regarding our products or services through any communication channel: app, website, email, phone, or physical visit. Some interactions may have additional privacy statements.
              </p>
              <p>
                For bookings at partner properties, those partners handle personal data according to their own privacy policies.
              </p>
            </div>
          </div>

          {/* 3 */}
          <div className="helpItem">
            <div className="helpIcon"><FaLock /></div>
            <div className="helpText">
              <h5>Data Handling & User Responsibility</h5>
              <p>
                This notice explains how YOVHO manages your data regardless of how you interact with us. Some app features may have their own explanations for data use.
              </p>
              <p>
                YOVHO-branded accommodations are clearly marked. Partner locations follow their own privacy policies.
              </p>
              <p>
                Our services are not intended for minors. Individuals under the legal adult age cannot book or stay in YOVHO accommodations unless accompanied by a responsible adult.
              </p>
              <p>
                For questions or concerns regarding data handling, contact us at <a href="mailto:info@yovho.com">info@yovho.com</a>.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
    <section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">How We Collect Personal Information</h2>
      <p class="text-muted">
        We gather personal data both directly from you and from third parties such as travel agents or individuals who make bookings on your behalf.
      </p>
    </div>

    <div class="helpContent">

      <p>
        If you share personal details about others (e.g., guests who will be staying with you), you are responsible for ensuring they are aware of this privacy policy and consent to the sharing of their data.
      </p>

      <h5>Personal information may be collected through your interactions via:</h5>
      <ul>
        <li>Phone calls (which may be recorded for compliance and quality monitoring)</li>
        <li>Email correspondence</li>
        <li>Our official websites and mobile apps</li>
        <li>Postal mail</li>
        <li>Social media platforms</li>
        <li>In-person meetings at YOVHO-branded accommodations or through YOVHO representatives</li>
      </ul>

      <h5>We also receive your data from third parties including:</h5>
      <ul>
        <li>Individuals or agents making bookings on your behalf</li>
        <li>Travel agencies through which reservations are made from other travel platforms</li>
        <li>Accommodation providers affiliated with us</li>
        <li>Service partners that support the delivery of our offerings</li>
        <li>If your role involves corporate travel, business partners or third-party sources may identify you as a contact for offering our services to your organization</li>
      </ul>

      <h5>Personal Data</h5>
      <p>Type of personal data we handle:</p>
      <ul>
        <li><strong>General Personal Data</strong> – Information used to identify, contact, and manage our relationship with you.</li>
        <li><strong>Sensitive Personal Data</strong> – Specific types of information protected under applicable law, such as health-related requests or religious preferences.</li>
      </ul>

      <p>Please see below for details.</p>
    </div>
  </div>
</section>


<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">General Personal Data</h2>
      <p class="text-muted">
        We collect the following types of general personal information to provide, improve, and secure our services:
      </p>
    </div>

    <div class="helpContent">
      <ul>
        <li>
          <strong>a. Contact Information:</strong>
          <p>Includes your name, address, phone number, email, date of birth, gender, nationality, and country of residence. If you link a social media profile, we may collect your profile picture.</p>
        </li>
        <li>
          <strong>b. Location Data:</strong>
          <p>We may estimate your location using your IP address or, if permitted, collect your precise device location through our app to suggest nearby accommodations.</p>
        </li>
        <li>
          <strong>c. Booking and Transaction Records:</strong>
          <p>We record details such as services requested, booking/stay dates, duration, payment amounts, and referral tracking (if applicable).</p>
        </li>
        <li>
          <strong>d. Account and Communication History:</strong>
          <p>We maintain a record of your bookings, complaints, support interactions, or incidents you’ve reported.</p>
        </li>
        <li>
          <strong>e. Usage and Technical Information:</strong>
          <p>This includes how you interact with our platforms (web/app), search terms, device type, IP address, crash reports, and browsing history. See our Cookie Policy for details.</p>
        </li>
        <li>
          <strong>f. Device Data:</strong>
          <p>We collect details about your mobile device and software to enhance performance, including system version, device ID, language preferences, and mobile network.</p>
        </li>
        <li>
          <strong>g. Call & SMS Logs:</strong>
          <p>For communication with property managers, we may log the date, time, and content of calls or messages made through our system.</p>
        </li>
        <li>
          <strong>h. CCTV:</strong>
          <p>Video footage may be recorded when you enter our premises to ensure safety and security.</p>
        </li>
        <li>
          <strong>i. Wi-Fi Access:</strong>
          <p>When you use Wi-Fi in YOVHO Partner properties, we collect login credentials and monitor usage patterns.</p>
        </li>
      </ul>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Sensitive Personal Data</h2>
      <p class="text-muted">
        In some cases, you may share personal details that fall under legally protected categories. These include:
      </p>
    </div>

    <div class="helpContent">
      <ul>
        <li>
          <strong>Dietary or Religious Preferences:</strong>
          <p>Such as requests for specific meals.</p>
        </li>
        <li>
          <strong>Medical or Accessibility Needs:</strong>
          <p>Such as mobility support or health-related accommodation requests.</p>
        </li>
        <li>
          <strong>Relationship Information:</strong>
          <p>If you disclose that you are traveling with a spouse or family member.</p>
        </li>
      </ul>

      <p>
        Please note that YOVHO accommodations are exclusively available to:
      </p>
      <ul>
        <li>Families</li>
        <li>Solo travelers</li>
        <li>Business travelers</li>
      </ul>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Unmarried Couples & Accommodation Policy</h2>
      <p class="text-muted">
        As part of our commitment to family-friendly and professional standards, unmarried couples are not permitted to book or share the same room at any YOVHO-branded property. We may verify guest relationships at check-in, and reservations that do not comply with this policy may be declined.
      </p>
      <p>
        YOVHO accommodations are intended exclusively for families, solo travelers, and business travelers. In accordance with our policy, unmarried couples are not permitted to book or share a room at YOVHO Partner properties. We reserve the right to verify guest relationships during the check-in process and may refuse accommodation where this policy is not met.
      </p>
    </div>

    <div class="helpContent">
      <h5>Purpose and Lawful Grounds for Processing Personal Information</h5>
      <p>
        YOVHO processes your personal data for the purposes outlined in this privacy notice, based on the lawful grounds provided under applicable data protection regulations. The legal basis for processing your information may vary depending on the type of data and the specific context of your interaction with us.
      </p>

      <h5>1. Standard Personal Information</h5>
      <p>We process standard personal information on the following legal grounds:</p>

      <ul>
        <li>
          <strong>a. Contractual Necessity:</strong>
          <p>
            Where a contract exists between you and YOVHO, we process your information as required to perform our contractual obligations. This includes managing bookings, providing accommodations, and delivering related services for you or others included in your reservation.
          </p>
        </li>

        <li>
          <strong>b. Legitimate Interests:</strong>
          <p>
            We may process your personal information where necessary for our or third parties’ legitimate interests, provided those interests are not overridden by your rights and freedoms. These interests may include:
          </p>
          <ul>
            <li>Enhancing and maintaining the quality of our services</li>
            <li>Responding to customer queries and complaints</li>
            <li>Preventing fraud and ensuring the security of our services</li>
            <li>Marketing and business analytics (with appropriate safeguards)</li>
          </ul>
        </li>

        <li>
          <strong>c. Legal Compliance:</strong>
          <p>
            We may also process your data where it is required or explicitly permitted under applicable law or regulation.
          </p>
        </li>
      </ul>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Special Category (Sensitive) Information</h2>
      <p class="text-muted">
        Special category data includes information revealing your health status, religious beliefs, or other protected characteristics under applicable law. This data is only processed under strict conditions, such as:
      </p>
    </div>

    <div class="helpContent">
      <ul>
        <li>
          <strong>a. Information Made Public by You:</strong>
          <p>If you have voluntarily disclosed special category information (e.g., dietary preferences based on religion or the nature of a celebration such as a wedding), we may process such data accordingly.</p>
        </li>

        <li>
          <strong>b. Substantial Public Interest (Based on Law):</strong>
          <p>In certain cases, such as a medical emergency or safety-related incident during your stay, we may process relevant information to ensure proper handling, documentation, and reporting.</p>
        </li>

        <li>
          <strong>c. Explicit Consent:</strong>
          <p>Where legally required, we will seek your clear and informed consent before processing any special category information. Consent is not implied by reading this privacy notice.</p>
        </li>

        <li>
          <strong>d. Legal Claims:</strong>
          <p>We may process special category information where necessary to establish, exercise, or defend legal claims.</p>
        </li>
      </ul>

      <h5>Legitimate Interest</h5>
      <p>
        We process your personal information for a range of legitimate business purposes. These include delivering our services, managing our relationship with you, improving our offerings, conducting marketing (where lawful), and protecting our legal rights. 
      </p>
      <p>
        We always ensure that our interests do not override your rights, freedoms, or reasonable expectations.
      </p>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Our Legitimate Interests</h2>
      <p class="text-muted">
        YOVHO processes personal information under legitimate interests for a variety of purposes, including service delivery, security, marketing, and business operations.
      </p>
    </div>

    <div class="helpContent">
      <ul>
        <li>
          <strong>a. Service Delivery and Administration:</strong>
          <p>To provide and manage the products and services you book with us, such as:</p>
          <ul>
            <li>Fulfilling accommodation and service requests</li>
            <li>Operating loyalty programs (where applicable)</li>
            <li>Sending service-related communications, including confirmations and updates</li>
            <li>Processing payments, generating receipts, and offering customer support</li>
          </ul>
        </li>

        <li>
          <strong>b. Relationship and Vendor Management:</strong>
          <p>To manage our interactions with you and our partners, including:</p>
          <ul>
            <li>Handling feedback or complaints</li>
            <li>Verifying service quality from third-party providers</li>
            <li>Ensuring accurate billing and service delivery</li>
          </ul>
        </li>

        <li>
          <strong>c. Security and Authentication:</strong>
          <p>To ensure secure and safe interactions with our platforms, such as:</p>
          <ul>
            <li>Verifying your identity</li>
            <li>Preventing unauthorized access to your account or our services</li>
          </ul>
        </li>

        <li>
          <strong>d. Product and Service Improvement:</strong>
          <p>To improve the YOVHO app, website, and services by:</p>
          <ul>
            <li>Diagnosing bugs or performance issues</li>
            <li>Conducting data analysis, testing, and customer research</li>
            <li>Monitoring usage trends and feedback</li>
          </ul>
        </li>

        <li>
          <strong>e. Marketing and Personalization:</strong>
          <p>To send you updates and promotional content we believe may interest you, including:</p>
          <ul>
            <li>Information about our services, promotions, news, and features</li>
            <li>Personalized content, recommendations, or advertisements</li>
          </ul>
          <p>You may opt out of marketing communications at any time using the unsubscribe link in our emails, your device settings, or by contacting our support team.</p>
        </li>

        <li>
          <strong>f. Fraud Prevention and Abuse Detection:</strong>
          <p>To monitor and prevent:</p>
          <ul>
            <li>Fraudulent transactions</li>
            <li>Misuse of our services or accounts</li>
          </ul>
        </li>

        <li>
          <strong>g. Enforcement of Terms and Protection of Rights:</strong>
          <p>To:</p>
          <ul>
            <li>Enforce our Terms of Use, policies, or other contractual obligations</li>
            <li>Safeguard the rights, property, and safety of YOVHO, our customers, and third parties</li>
          </ul>
        </li>

        <li>
          <strong>h. Legal Defense:</strong>
          <p>To:</p>
          <ul>
            <li>Exercise our legal rights</li>
            <li>Defend ourselves against legal claims or investigations</li>
          </ul>
        </li>

        <li>
          <strong>i. Business Transactions:</strong>
          <p>To facilitate or undergo mergers, acquisitions, sales, or restructuring involving all or part of YOVHO’s business or assets.</p>
        </li>
      </ul>

      <h5>Marketing and Preferences</h5>
      <p>
        YOVHO may use your personal information to provide you with marketing communications through a variety of channels, including:
      </p>
      <ul>
        <li>Email</li>
        <li>SMS</li>
        <li>Telephone</li>
        <li>Postal mail</li>
        <li>Application notifications (including silent notifications for tracking app installation status)</li>
        <li>Social media platforms</li>
        <li>Online advertising, including retargeting and personalized campaigns</li>
      </ul>
      <p>
        We may also use data analytics and technology to understand your preferences and interests in order to tailor our marketing content and recommendations, making them more relevant to you.
      </p>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Managing Your Marketing Preferences</h2>
      <p class="text-muted">
        You can manage your marketing preferences at any time. If you wish to opt out of receiving marketing emails, you can click the "unsubscribe" link included in every marketing email. Additionally, you may:
      </p>
    </div>

    <div class="helpContent">
      <ul>
        <li>Adjust your device settings to disable app notifications</li>
        <li>Contact us to update or change your contact preferences</li>
        <li>Object to direct marketing or related profiling by reaching out to us</li>
      </ul>

      <p>For any of the above, please contact us at:</p>
      <p>📧 <a href="mailto:dataprotection@yovho.com">dataprotection@yovho.com</a>, <a href="mailto:info@yovho.com">info@yovho.com</a></p>

      <h5>Cookies and Other Tracking Technologies</h5>
      <p>
        YOVHO and its third-party service providers use cookies and similar tracking technologies to collect information about how you interact with our websites and mobile applications. These technologies help us to:
      </p>
      <ul>
        <li>Understand user behavior and preferences</li>
        <li>Improve website functionality and performance</li>
        <li>Deliver personalized content and advertisements</li>
        <li>Measure the effectiveness of our marketing campaigns</li>
      </ul>
      <p>
        For detailed information about how we use cookies and how you can manage your cookie preferences, please refer to our <a href="#">Cookie Statement</a>.
      </p>

      <h5>Do-Not-Track Signals</h5>
      <p>
        At this time, our systems do not respond to browser Do-Not-Track (DNT) signals. If you wish to limit tracking, you may:
      </p>
      <ul>
        <li>Adjust your browser settings to disable or manage cookies</li>
        <li>Follow the opt-out instructions provided in our Cookie Statement</li>
        <li>Use the opt-out mechanisms provided by third-party service providers directly</li>
      </ul>
      <p>We continuously review and update our privacy and tracking practices in line with legal requirements and industry standards.</p>

      <h5>Analytics and Advertising Services Provided by Third Parties</h5>
      <p>
        YOVHO engages trusted third-party service providers to deliver audience measurement, analytics, and advertising services. These providers help us better understand how users interact with our websites and mobile applications, enable personalized marketing experiences, and assess the effectiveness of our advertising campaigns across various digital platforms.
      </p>
      <p>
        To enhance user experience and relevance, we also utilize the Google Places API, which offers location-based suggestions based on user input or device location.
      </p>
      <p>We may permit selected third parties to:</p>
      <ul>
        <li>Perform web and app analytics on our behalf</li>
        <li>Deliver interest-based advertisements across websites, apps, and social platforms</li>
        <li>Monitor and report on the performance of our advertisements</li>
      </ul>
      <p>
        These third-party providers may use cookies, web beacons, pixels, SDKs, and other similar tracking technologies to recognize your device and collect data when you access YOVHO services or interact with other digital platforms.
      </p>
      <p>
        For a comprehensive overview of these technologies and how to manage or disable them, please refer to our <a href="#">Cookie Statement</a>.
      </p>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Google Services</h2>
      <p class="text-muted">
        YOVHO integrates the Google Places API to provide predictive place suggestions and enhanced search functionality. By using this feature, you are also subject to:
      </p>
      <ul>
        <li><a href="https://www.google.com/intl/en/policies/terms/" target="_blank">Google Maps Terms Of Service</a></li>
        <li><a href="https://policies.google.com/privacy" target="_blank">Google Privacy Policy</a></li>
      </ul>
      <p>
        To prevent your data from being used by Google Analytics, you may install the official Google Analytics Opt-out Browser Add-on.
      </p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Sharing Your Information</h2>
      <p class="text-muted">
        We share your personal information within the YOVHO Group and with carefully selected third parties who support the delivery of our services. We also disclose information where required by applicable law or in accordance with regulatory obligations. Below is a detailed overview of the types of entities your information may be shared with:
      </p>
    </div>

    <div class="helpContent">
      <ul>
        <li>
          <strong>Other entities within the YOVHO Group:</strong> To support internal operations and provide you with a seamless experience across our global services.
        </li>
        <li>
          <strong>Accommodation providers and managers:</strong> To fulfill your booking and accommodation preferences. For example, we share your name, check-in details, and special requests directly with the accommodation provider.
        </li>
        <li>
          <strong>Authorized service providers and business partners:</strong> These include entities who assist us in delivering our services, technology infrastructure, customer support, payment processing, marketing, or promotional campaigns. This also includes third-party platforms that integrate with our services or that we integrate with.
        </li>
        <li>
          <strong>Referral contacts:</strong> If you use features that allow you to refer friends, contacts, or colleagues, we may share your name with those individuals.
        </li>
        <li>
          <strong>Public forums and social features:</strong> If you submit content (such as reviews or comments) on public platforms associated with YOVHO or engage with features that allow you to share content via social media or other networks, that content may become visible to the general public based on your privacy settings. Please review the privacy policies of any third-party platforms you engage with.
        </li>
        <li>
          <strong>Legal and regulatory authorities:</strong> Where required, we may disclose your information to law enforcement, governmental agencies, regulatory bodies, or other authorities in accordance with legal obligations or lawful requests.
        </li>
        <li>
          <strong>Judicial or police authorities:</strong> We may disclose data if required to comply with a court order, enforce our rights, or cooperate in a lawful investigation.
        </li>
        <li>
          <strong>Parties to a business transaction:</strong> In the event of a potential or actual sale, merger, acquisition, financing, restructuring, or transfer of all or part of the YOVHO business or its assets, your data may be shared with relevant third parties involved in the transaction.
        </li>
        <li>
          <strong>Aggregated or anonymised data recipients:</strong> We may share data that has been aggregated, pseudonymised, or anonymised in such a way that it cannot reasonably be used to identify any individual. This data may be used for analytics, research, or reporting purposes.
        </li>
      </ul>
    </div>
  </div>
</section>
<section class="helpSection p-3">
  <div class="container-xl">
    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Questions and Contact Information</h2>
      <p>
        If you have any questions, comments, concerns, or complaints regarding this Privacy Notice or our handling of your personal information, please feel free to contact our Privacy Team.
      </p>
      <p>Email: <a href="mailto:privacy@yovho.com">privacy@yovho.com</a></p>
      <p>We are committed to addressing your concerns promptly and transparently.</p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Changes to This Privacy Notice</h2>
      <p>
        This Privacy Notice is effective as of the "Last Updated" date indicated above. YOVHO may revise or update this Privacy Notice from time to time to reflect changes in our practices, legal or regulatory requirements, or improvements to our services.
      </p>
      <p>
        We encourage you to review this notice periodically. Updates will be posted on our website and mobile application. If the changes are material, we will provide a more prominent notice or seek your consent where required by applicable law.
      </p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Additional Information for Individuals in the European Union (EU) and EEA</h2>
      <p>
        If you are located in the EU or EEA, the following additional information applies to you under the GDPR:
      </p>
      <ul>
        <li><strong>Data Controller:</strong> The entity responsible for the processing of your personal data is YOVHO.</li>
        <li><strong>Your Rights:</strong> You have rights to access, rectify, erase, restrict or object to processing, data portability, withdraw consent, and lodge a complaint with a supervisory authority.</li>
        <li><strong>Legal Basis for Processing:</strong> We process your personal data only where we have a valid legal basis, including contract performance, legal obligations, consent, or legitimate interests.</li>
        <li><strong>International Transfers:</strong> If your personal data is transferred outside the EU/EEA, YOVHO ensures adequate safeguards, such as Standard Contractual Clauses.</li>
      </ul>
      <p>For any requests regarding your GDPR rights, please contact: <a href="mailto:privacy@yovho.com">privacy@yovho.com</a></p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">International Data Transfers</h2>
      <p>
        YOVHO operates global systems, so your data may be transferred outside your country of residence, including countries like Pakistan and UAE. While these countries may not offer the same level of protection as the EEA, YOVHO implements safeguards such as:
      </p>
      <ul>
        <li>Standard Contractual Clauses approved by the European Commission</li>
        <li>Binding Corporate Rules (BCRs), where applicable</li>
        <li>Other lawful transfer mechanisms ensuring protection equivalent to EEA standards</li>
      </ul>
      <p>For more details, contact: <a href="mailto:privacy@yovho.com">privacy@yovho.com</a></p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Data Retention</h2>
      <p>
        We retain personal information according to YOVHO’s policies and legal obligations. Retention periods may vary depending on:
      </p>
      <ul>
        <li>Duration of your relationship with YOVHO</li>
        <li>Need to maintain records for legal, contractual, or regulatory compliance</li>
        <li>Applicable statutory limitation periods</li>
        <li>Retention requirements from laws or regulatory authorities</li>
        <li>Existence of disputes, complaints, audits, or investigations</li>
      </ul>
      <p>Once data is no longer required and not legally obligated to retain, it will be securely deleted or anonymized.</p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Your Legal Rights</h2>
      <p>
        Individuals may have rights regarding personal information under data protection laws. These rights include:
      </p>
      <ul>
        <li><strong>Access:</strong> Request details of personal information we hold and obtain a copy.</li>
        <li><strong>Rectification:</strong> Correct inaccurate or incomplete personal information.</li>
        <li><strong>Erasure:</strong> Request deletion of certain personal information ("Right to be Forgotten").</li>
        <li><strong>Restriction of Processing:</strong> Limit use of your personal information.</li>
        <li><strong>Object:</strong> Object to processing, including for marketing purposes.</li>
        <li><strong>Data Portability:</strong> Request transfer of your personal information in a structured, machine-readable format.</li>
        <li><strong>Withdraw Consent:</strong> Withdraw consent at any time (without affecting previous lawful processing).</li>
      </ul>
      <p>
        To exercise your rights, contact YOVHO Privacy Team: <a href="mailto:dataprotection@yovho.com">dataprotection@yovho.com</a>
      </p>
    </div>

    <div class="helpHeader mb-4">
      <h2 class="fw-bold text-dark">Data Protection Contacts</h2>
      <p>
        For any questions, complaints, or concerns regarding this Privacy Notice or the handling of your personal information, contact YOVHO’s Data Protection Officer and Privacy Team:
      </p>
      <ul>
        <li>Email: <a href="mailto:dataprotection@yovho.com">dataprotection@yovho.com</a></li>
      </ul>
      <p>
        You also have the right to lodge a complaint with your local data protection authority if you are in the EU.
      </p>
    </div>
  </div>
</section>

</>
  );
};

export default HelpPrivacyNotice;
